import React from 'react';
import { StaticMapProviderProps, StaticMapMarker, StaticMapUrlParams } from './StaticMapInterface';
import { LatLng, UniversalMapEntry } from './MapInterface';

/**
 * GoogleStaticImage Component
 * 
 * Renders static map images using Google Maps Static API
 * https://developers.google.com/maps/documentation/maps-static/overview
 */
const GoogleStaticImage: React.FC<StaticMapProviderProps> = ({
  entries,
  center,
  zoom = 10,
  width,
  height,
  showMarkers = true,
  className,
  style,
  onError,
  onLoad,
  alt = 'Google Maps static map',
  apiKey,
  baseUrl,
  mapType = 'roadmap',
}) => {
  /**
   * Get custom pin URL for markers
   * Google Maps requires HTTPS URLs for custom icons
   */
  const getCustomPinUrl = (): string => {
    const isRetina = window.devicePixelRatio > 1;
    const pinFile = isRetina ? 'map_pin64x64.png' : 'map_pin32x32.png';
    
    // Google Maps requires HTTPS and can't access localhost
    if (baseUrl.includes('localhost') || baseUrl.startsWith('http://')) {
      // Fallback to null for localhost/HTTP - will use standard markers
      return '';
    }
    
    return `${baseUrl}/${pinFile}`;
  };

  /**
   * Calculate center point from entries if not provided
   */
  const calculateCenter = (entries: UniversalMapEntry[]): LatLng => {
    if (entries.length === 0) {
      return { lat: 39.8283, lng: -98.5795 }; // Center of US as fallback
    }

    const sum = entries.reduce(
      (acc, entry) => ({
        lat: acc.lat + entry.location.lat,
        lng: acc.lng + entry.location.lng,
      }),
      { lat: 0, lng: 0 }
    );

    return {
      lat: sum.lat / entries.length,
      lng: sum.lng / entries.length,
    };
  };

  /**
   * Convert entries to static map markers
   */
  const entriesToMarkers = (entries: UniversalMapEntry[]): StaticMapMarker[] => {
    const customPinUrl = getCustomPinUrl();
    
    return entries.map((entry) => ({
      position: { lat: entry.location.lat, lng: entry.location.lng },
      iconUrl: customPinUrl || undefined, // Use undefined instead of empty string
      label: entry.title || '',
      color: 'red', // Default color for fallback
    }));
  };

  /**
   * Generate Google Maps static map URL
   */
  const generateGoogleUrl = (params: StaticMapUrlParams): string => {
    const { center, zoom, width, height, markers, apiKey, scale = 1 } = params;
    
    // Base URL for Google Maps Static API
    const baseGoogleUrl = 'https://maps.googleapis.com/maps/api/staticmap';
    
    // Build URL parameters
    const urlParams = new URLSearchParams();
    urlParams.set('center', `${center.lat},${center.lng}`);
    urlParams.set('zoom', zoom.toString());
    urlParams.set('size', `${width}x${height}`);
    urlParams.set('maptype', mapType);
    urlParams.set('format', 'png');
    urlParams.set('key', apiKey);
    
    // Add scale for retina displays
    if (scale > 1) {
      urlParams.set('scale', scale.toString());
    }
    
    // Add markers if present
    if (markers.length > 0 && showMarkers) {
      // Group markers to optimize URL length
      const customMarkers: string[] = [];
      const standardMarkers: string[] = [];
      
      markers.forEach((marker) => {
        if (marker.iconUrl && !marker.iconUrl.includes('localhost')) {
          // Custom icon marker (avoid localhost URLs which Google can't access)
          customMarkers.push(`${marker.position.lat},${marker.position.lng}`);
        } else {
          // Standard colored marker as fallback
          const color = marker.color || 'red';
          standardMarkers.push(`${marker.position.lat},${marker.position.lng}`);
        }
      });
      
      // Add custom icon markers
      if (customMarkers.length > 0 && markers[0]?.iconUrl && !markers[0].iconUrl.includes('localhost')) {
        const iconParam = `icon:${markers[0].iconUrl}`;
        const markerParam = `${iconParam}|${customMarkers.join('|')}`;
        urlParams.append('markers', markerParam);
      }
      
      // Add standard markers
      if (standardMarkers.length > 0) {
        const markerParam = `color:red|size:mid|${standardMarkers.join('|')}`;
        urlParams.append('markers', markerParam);
      }
    }
    
    const url = `${baseGoogleUrl}?${urlParams.toString()}`;
    
    // Check URL length (Google has a limit of ~8192 characters)
    if (url.length > 8000) {
      console.warn('Google Maps Static API URL is approaching length limit:', url.length);
      // Fallback: use standard markers only
      const fallbackParams = new URLSearchParams();
      fallbackParams.set('center', `${center.lat},${center.lng}`);
      fallbackParams.set('zoom', zoom.toString());
      fallbackParams.set('size', `${width}x${height}`);
      fallbackParams.set('maptype', mapType);
      fallbackParams.set('format', 'png');
      fallbackParams.set('key', apiKey);
      if (scale > 1) fallbackParams.set('scale', scale.toString());
      if (markers.length > 0) {
        const positions = markers.slice(0, 10).map(m => `${m.position.lat},${m.position.lng}`);
        fallbackParams.append('markers', `color:red|size:mid|${positions.join('|')}`);
      }
      return `${baseGoogleUrl}?${fallbackParams.toString()}`;
    }
    
    return url;
  };

  /**
   * Handle image load error
   */
  const handleImageError = () => {
    const errorMessage = 'Failed to load Google Maps static map image';
    console.error(errorMessage);
    if (onError) {
      onError(new Error(errorMessage));
    }
  };

  /**
   * Handle image load success
   */
  const handleImageLoad = () => {
    if (onLoad) {
      onLoad();
    }
  };

  // Validate API key
  if (!apiKey) {
    const errorMessage = 'Google Maps API key is required';
    console.error(errorMessage);
    if (onError) {
      onError(new Error(errorMessage));
    }
    return null;
  }

  // Calculate parameters
  const mapCenter = center || calculateCenter(entries);
  const markers = showMarkers ? entriesToMarkers(entries) : [];
  const scale = window.devicePixelRatio > 1 ? 2 : 1;

  // Generate URL parameters
  const urlParams: StaticMapUrlParams = {
    center: mapCenter,
    zoom,
    width,
    height,
    markers,
    apiKey,
    scale,
  };

  // Generate the static map URL
  const mapUrl = generateGoogleUrl(urlParams);

  // Log URL for debugging (remove in production)
  if (process.env.NODE_ENV === 'development') {
    console.log('Google Maps Static URL:', mapUrl);
  }

  return (
    <img
      src={mapUrl}
      alt={alt}
      className={className}
      style={{
        display: 'block',
        ...style, // Let style override width/height
      }}
      onError={handleImageError}
      onLoad={handleImageLoad}
    />
  );
};

export default GoogleStaticImage;